import { TranslocoPipe, provideTranslocoScope } from '@jsverse/transloco';
import { Component, Inject, inject, signal, Type } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { PriceBelowMinimumWarning } from '../price-below-minimum-warning/price-below-minimum-warning';
import { RemoveFromCartDialog, RemoveFromCartDialogData } from '../remove-from-cart-dialog/remove-from-cart-dialog';
import { ProductPurchase } from '../../../interfaces/product.model';
import { CartService } from '../../../services/cart.service';

export interface AddToCartDialogData {
  idProdotto: string;
  nomeProdotto: string;
  sku?: string;
  nomeImballo?: string;
  unitaFatturabile?: number;
  prezzo: number;
  unitaMisura?: string;
  prezzoMinimo?: number;
  ultimoAcquisto?: ProductPurchase | null;
}

export interface AddToCartDialogResult {
  // 0 = il prodotto va tolto dal carrello
  quantity: number;
  price: number;
}

@Component({
  providers: [provideTranslocoScope('products')],
  selector: 'prd-add-to-cart-dialog',
  imports: [
    TranslocoPipe,
    NgComponentOutlet,
    MatDialogModule,
    MatButtonModule,
    MatDividerModule,
    MatFormFieldModule,
    MatInputModule,
    PriceBelowMinimumWarning,
  ],
  templateUrl: './add-to-cart-dialog.html',
  styleUrl: './add-to-cart-dialog.scss',
})
export class AddToCartDialog {
  private dialog = inject(MatDialog);
  private cartService = inject(CartService);

  protected quantity = signal(1);
  protected price = signal(0);
  // Prodotto già in carrello: si riparte da quantità e prezzo della riga, la conferma li sostituisce
  protected inCart = false;

  constructor(
    private dialogRef: MatDialogRef<AddToCartDialog, AddToCartDialogResult>,
    @Inject(MAT_DIALOG_DATA) readonly data: AddToCartDialogData,
  ) {
    const row = this.cartService.cartRow(data.idProdotto);
    this.inCart = !!row;
    this.quantity.set(row?.quantita ?? 1);
    this.price.set(row?.prezzo ?? data.prezzo);
  }

  // Contenuto sotto il prezzo, per i dialog che estendono questo
  protected extra(): Type<unknown> | null {
    return null;
  }

  protected extraInputs(): Record<string, unknown> {
    return {};
  }

  // Stesso formato della colonna imballo della tabella prodotti, es. "Fusto - 208 - L"
  get packagingDescription(): string {
    return [this.data.nomeImballo, this.data.unitaFatturabile, this.data.unitaMisura]
      .filter(part => part !== undefined && part !== null && part !== '')
      .join(' - ');
  }

  onPriceInput(value: string) {
    const parsed = parseFloat(value);
    this.price.set(Number.isNaN(parsed) || parsed < 0 ? this.data.prezzo : parsed);
  }

  increment() {
    this.quantity.update(q => q + 1);
  }

  decrement() {
    this.quantity.update(q => Math.max(1, q - 1));
  }

  onQuantityInput(value: string) {
    const parsed = parseInt(value, 10);
    this.quantity.set(Number.isNaN(parsed) || parsed < 1 ? 1 : parsed);
  }

  onClose() {
    this.dialogRef.close();
  }

  // Il cestino toglie la riga già in carrello, con la stessa conferma del pannello carrello
  onRemove() {
    if (!this.inCart) {
      this.onClose();
      return;
    }

    const data: RemoveFromCartDialogData = {
      nomeProdotto: this.data.nomeProdotto,
      nomeImballo: this.data.nomeImballo,
      unitaFatturabile: this.data.unitaFatturabile,
      unitaMisura: this.data.unitaMisura,
    };

    this.dialog.open(RemoveFromCartDialog, {
      width: '400px',
      data,
    }).afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.dialogRef.close({ quantity: 0, price: this.price() });
      }
    });
  }

  onConfirm() {
    this.dialogRef.close({ quantity: this.quantity(), price: this.price() });
  }
}
